import { useState, useEffect } from 'react';
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card';
import { Button } from '@shadcn/components/ui/button';

import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';
import { toast } from 'react-toastify';

const AmazonStep = ({
  amazonLinks,
  setAmazonLinks,
  onSave,
  onNext
}) => {
  const [loading, setLoading] = useState(false);
  const [amazonConfigs, setAmazonConfigs] = useState([]);
  const [selectedConfigs, setSelectedConfigs] = useState([]);

  
  useEffect(() => {
    fetchAmazonConfigs();
  }, []);

  
  useEffect(() => {
    if (amazonLinks && amazonLinks.length > 0) {
      const configIds = amazonLinks.map(link => link.configId);
      setSelectedConfigs(configIds);
    }
  }, [amazonLinks]);

  const fetchAmazonConfigs = () => {
    setLoading(true);
    superagent
      .get(`${backendUrl}/amazon-configs`)
      .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
      .set('accept', 'json')
      .then((response) => {
        if (response.status <= 400) {
          setAmazonConfigs(response.body.data || []);
        } else {
          toast.error('Failed to fetch Amazon configurations');
        }
      })
      .catch((err) => {
        console.error('Error fetching Amazon configs:', err);
        toast.error('Error fetching Amazon configurations');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleConfigToggle = (configId) => {
    setSelectedConfigs(prev => {
      const newSelections = prev.includes(configId)
        ? prev.filter(id => id !== configId)
        : [...prev, configId];
      
      return newSelections;
    });
  };

  const handleSave = () => {
    if (selectedConfigs.length === 0) {
      toast.error('Please select at least one Amazon configuration');
      return false;
    }
    
    
    const newAmazonLinks = selectedConfigs.map(configId => {
      const config = amazonConfigs.find(c => c._id === configId);
      return {
        configId,
        storeKey: config?.storeKey || '',
        region: config?.region || '',
        syncType: 'NONE',
        marketplaces: config?.marketplaces || []
      };
    });
    setAmazonLinks(newAmazonLinks);
    
    
    return true;
  };

  const handleNext = () => {
    const saveSuccessful = handleSave();
    if (saveSuccessful && onNext) {
      onNext();
    }
  };

  const renderConfigCard = (config) => {
    const isSelected = selectedConfigs.includes(config._id);
    
    return (
      <Card
        key={config._id}
        className={`relative cursor-pointer transition-all duration-300 hover:shadow-xl transform hover:scale-[1.03] overflow-hidden ${
          isSelected
            ? 'border-orange-500 shadow-lg ring-2 ring-orange-200 bg-gradient-to-br from-orange-50 to-white'
            : 'border-gray-200 hover:border-orange-300 hover:shadow-md bg-white'
        }`}
        onClick={() => handleConfigToggle(config._id)}
      >
        {isSelected && (
          <div className="absolute top-3 right-3 bg-orange-500 text-white rounded-full p-1.5 z-10 shadow-lg animate-pulse">
            <i className="bx bx-check text-sm"></i>
          </div>
        )}
        
        <div className={`absolute top-0 left-0 w-full h-1 ${
          isSelected ? 'bg-gradient-to-r from-orange-400 to-orange-600' : 'bg-gradient-to-r from-gray-200 to-gray-300'
        }`}></div>
        
        <CardBody className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-3 rounded-xl shadow-sm ${
              isSelected ? 'bg-gradient-to-br from-orange-100 to-orange-50' : 'bg-gradient-to-br from-gray-100 to-gray-50'
            }`}>
              <i className={`bx bxl-amazon text-2xl ${
                isSelected ? 'text-orange-600' : 'text-gray-700'
              }`}></i>
            </div>
            <div className="flex-1">
              <h5 className="font-bold text-gray-800 m-0 text-lg">{config.storeKey}</h5>
              <div className={`text-xs font-medium mt-1 px-2 py-0.5 rounded-full inline-block ${
                isSelected ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
              }`}>
                Amazon Seller
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded-lg">
              <i className="bx bx-globe text-orange-500"></i>
              <span className="text-gray-600 font-medium">Region:</span>
              <span className="font-bold text-gray-800">{config.region}</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded-lg">
              <i className="bx bx-store text-orange-500"></i>
              <span className="text-gray-600 font-medium">Marketplaces:</span>
              <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-bold">
                {config.marketplaces?.length || 0}
              </span>
            </div>
            
            {config.marketplaces && config.marketplaces.length > 0 && (
              <div className="border-t pt-3 mt-3">
                <div className="text-xs font-semibold text-gray-500 mb-2">ACTIVE MARKETPLACES</div>
                <div className="space-y-2">
                  {config.marketplaces.slice(0, 2).map((marketplace) => (
                    <div key={marketplace.marketplaceId} className="text-xs bg-gradient-to-r from-gray-50 to-white p-2 rounded-lg border border-gray-100">
                      <div className="font-bold text-gray-700 flex items-center gap-1">
                        <span className={`inline-block w-4 h-4 rounded-full bg-gradient-to-br from-orange-400 to-orange-600`}></span>
                        {marketplace.name} ({marketplace.countryCode})
                      </div>
                      <div className="text-gray-500 mt-1">{marketplace.domainName}</div>
                    </div>
                  ))}
                  {config.marketplaces.length > 2 && (
                    <div className="text-xs text-gray-500 text-center pt-1 bg-gray-50 rounded-lg py-2">
                      <i className="bx bx-plus-circle"></i> +{config.marketplaces.length - 2} more marketplace{config.marketplaces.length - 2 > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    );
  };

  return (
    <div className="p-2 md:p-6">
      <div className="flex items-center gap-3 mb-6 md:mb-8">
        <div className="p-3 bg-gradient-to-br from-orange-100 to-orange-50 rounded-xl shadow-sm">
          <i className="bx bxl-amazon text-orange-600 text-2xl"></i>
        </div>
        <div>
          <h4 className="text-xl md:text-2xl font-bold text-gray-800">Amazon Integration</h4>
          <p className="text-sm text-gray-500">Connect your Amazon seller accounts</p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <i className="bx bx-info-circle text-blue-600 text-xl"></i>
            </div>
            <div className="text-sm">
              <p className="font-bold text-blue-800 mb-1">Amazon Integration:</p>
              <p className="text-blue-700">Select one or more Amazon configurations to connect with this location. Amazon integration uses default sync settings optimized for your workflow.</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h5 className="font-bold text-gray-800 text-lg">Available Amazon Configurations</h5>
            <p className="text-sm text-gray-500">Choose which Amazon accounts to connect</p>
          </div>
          <Button
            onClick={fetchAmazonConfigs}
            variant="outline"
            size="sm"
            disabled={loading}
            className="shadow-sm hover:shadow-md transition-shadow"
          >
            <i className={`bx ${loading ? 'bx-loader-alt bx-spin' : 'bx-refresh'} mr-2`}></i>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="text-center">
              <div className="spinner-border text-orange-500 m-1 mb-3" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="text-sm text-gray-500">Loading Amazon configurations...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {amazonConfigs.map(renderConfigCard)}
          </div>
        )}
        
        {amazonConfigs.length === 0 && !loading && (
          <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="bx bxl-amazon text-3xl text-gray-400"></i>
            </div>
            <p className="text-gray-700 font-bold text-lg mb-2">No Amazon configurations found</p>
            <p className="text-sm text-gray-500 mb-4">Please create Amazon configurations first before connecting to locations</p>
            <Button variant="outline" size="sm" className="shadow-sm">
              <i className="bx bx-plus-circle mr-2"></i>
              Create Amazon Configuration
            </Button>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          {selectedConfigs.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full font-medium">
                {selectedConfigs.length} configuration{selectedConfigs.length > 1 ? 's' : ''} selected
              </div>
              <span className="text-gray-500">Ready to connect</span>
            </div>
          )}
        </div>
        <Button
          onClick={handleNext}
          disabled={selectedConfigs.length === 0}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 px-6"
        >
          <i className="bx bx-arrow-right mr-2"></i>
          Continue to Next Step
        </Button>
      </div>
    </div>
  );
};

export default AmazonStep;